import { useCallback, useEffect, useRef, useState } from 'react';

import applicationsService from 'features/applications/applicationsService';

import useErrorCatch from 'hooks/useErrorCatch';

import { InterviewDetails } from 'types/interviewSlots';

/** Единственный источник id — переданный вызывающим кодом (split-view или сайдбар). */
export const useInterviewState = (applicationId: string | null) => {
  const catchError = useErrorCatch();

  const latestFetchIdRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [interviewRequest, setInterviewRequest] =
    useState<InterviewDetails | null>(null);
  const [interviewHistoryLoaded, setInterviewHistoryLoaded] = useState(false);

  const fetchInterviewRequest = useCallback(
    async (explicitApplicationId?: string | null): Promise<void> => {
      const targetId = explicitApplicationId ?? applicationId;
      if (!targetId) {
        return;
      }

      const fetchId = ++latestFetchIdRef.current;
      setInterviewHistoryLoaded(false);

      try {
        const response =
          await applicationsService.getApplicationInterviewRequest(targetId);

        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        setInterviewRequest(response.at(-1) || null);
        setInterviewHistoryLoaded(true);
      } catch (error) {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        catchError({ error });
        setInterviewHistoryLoaded(true);
      }
    },
    [applicationId, catchError]
  );

  useEffect(() => {
    if (!applicationId) {
      latestFetchIdRef.current += 1;
      setInterviewRequest(null);
      setInterviewHistoryLoaded(true);
      return;
    }

    void fetchInterviewRequest(applicationId);
  }, [applicationId, fetchInterviewRequest]);

  return {
    isLoading,
    setIsLoading,
    interviewRequest,
    interviewHistoryLoaded,
    fetchInterviewRequest
  };
};
